import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Minus, Plus, Package, ArrowRight, Calculator } from "lucide-react";

export default function Volumenrechner({ embedded = false, onResult }) {
  const [catalog, setCatalog] = useState({});
  const [qty, setQty] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/volume-catalog").then(({ data }) => setCatalog(data.catalog));
  }, []);

  const change = (key, delta) => setQty((q) => ({ ...q, [key]: Math.max(0, (q[key] || 0) + delta) }));

  let total = 0;
  const items = [];
  Object.values(catalog).flat().forEach((it) => {
    const n = qty[it.key] || 0;
    if (n > 0) { total += n * it.volume; items.push({ ...it, qty: n, volume: it.volume }); }
  });
  total = Math.round(total * 10) / 10;

  const proceed = () => {
    if (onResult) return onResult(total, items);
    const p = new URLSearchParams({ volumen: String(total), items: encodeURIComponent(JSON.stringify(items)) });
    nav(`/anfrage?${p.toString()}`);
  };

  return (
    <div className={embedded ? "" : "eb-container py-12"}>
      {!embedded && (<>
        <span className="eb-label text-[#FF5A1F]">Kostenlos & unverbindlich</span>
        <h1 className="font-head text-3xl lg:text-4xl font-bold mt-2 mb-2">Volumenrechner</h1>
        <p className="text-slate-400 mb-8 max-w-2xl">Wähle deine Möbel und Kartons – wir berechnen das geschätzte Transportvolumen in m³.</p>
      </>)}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {Object.entries(catalog).map(([room, list]) => (
            <div key={room} className="eb-card p-6" data-testid={`vol-room-${room}`}>
              <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Package className="w-4 h-4 text-[#FF5A1F]" /> {room}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {list.map((it) => (
                  <div key={it.key} className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2">
                    <div><div className="text-sm text-slate-200">{it.name}</div><div className="text-xs text-slate-500 mono">{it.volume} m³</div></div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => change(it.key, -1)} className="w-7 h-7 rounded-md bg-slate-800 hover:bg-slate-700 flex items-center justify-center" data-testid={`vol-minus-${it.key}`}><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-6 text-center mono text-sm" data-testid={`vol-qty-${it.key}`}>{qty[it.key] || 0}</span>
                      <button onClick={() => change(it.key, 1)} className="w-7 h-7 rounded-md bg-[#FF5A1F] hover:bg-[#E04810] flex items-center justify-center" data-testid={`vol-plus-${it.key}`}><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <div className="eb-card p-6" data-testid="vol-summary">
            <div className="w-11 h-11 rounded-lg bg-[#FF5A1F]/10 flex items-center justify-center mb-4"><Calculator className="w-5 h-5 text-[#FF5A1F]" /></div>
            <div className="eb-label">Geschätztes Volumen</div>
            <div className="font-head text-5xl font-extrabold text-[#FF5A1F] mt-1 mono" data-testid="vol-total">{total} m³</div>
            <div className="mt-5 space-y-1.5 max-h-52 overflow-auto">
              {items.length === 0 ? <p className="text-sm text-slate-500">Noch keine Artikel gewählt.</p> :
                items.map((it) => (
                  <div key={it.key} className="flex justify-between text-sm text-slate-400">
                    <span>{it.qty}× {it.name}</span><span className="mono">{Math.round(it.qty * it.volume * 10) / 10} m³</span>
                  </div>
                ))}
            </div>
            <button onClick={proceed} disabled={total <= 0} className="eb-btn-primary w-full mt-6" data-testid="vol-proceed-btn">
              Beiladung für {total} m³ finden <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
